import { useMemo } from 'react';

import { useBusinessEntity } from '@zenbusiness/application-commons-business-entity';
import { useExperimentFlag } from '@zenbusiness/application-commons-experiments';

import {
  ENABLE_SALES_TAX_PRODUCT,
  ENABLE_SCORP_BUNDLE_V1,
  RELEASE_SQUARESPACE_DOMAINS_RESELLER,
  RELEASE_SQUARESPACE_WEBSITES_RESELLER,
} from '#client/constants/experimentsFlags';
import { PRODUCT_SERVICE_NAME } from '#client/pages/Home/constants';
import { useWebsiteEligibility } from '#client/pages/ProductLanding/hooks/useWebsiteEligibility';

/**
 * Maps product service codes to their corresponding feature flag constants.
 * Products not in this map are always shown regardless of feature flag state.
 * Products in this map are only shown when their corresponding feature flag is enabled.
 *
 * To add a new feature-flagged product:
 * 1. Add the serviceCode as a key with the feature flag constant as the value
 * 2. Add the feature flag constant to experimentsFlags.js
 * 3. Update useProductCardFeatureFlags() to include the new flag
 * 4. If the new flag also replaces an existing card (e.g. the new variant
 *    supersedes a legacy serviceCode), add the legacy serviceCode to
 *    PRODUCT_CARD_DISABLED_FEATURE_FLAGS under the same flag so the legacy
 *    card is hidden when the flag is enabled.
 *
 * @type {Record<string, string>}
 */
export const PRODUCT_CARD_FEATURE_FLAGS = {
  s_corp_bundle: ENABLE_SCORP_BUNDLE_V1,
  sales_tax_registration: ENABLE_SALES_TAX_PRODUCT,
  [PRODUCT_SERVICE_NAME.DOMAIN_NAME_REG_SQSP]: RELEASE_SQUARESPACE_DOMAINS_RESELLER,
  [PRODUCT_SERVICE_NAME.STATIC_WEBSITE_SQSP]: RELEASE_SQUARESPACE_WEBSITES_RESELLER,
};

/**
 * Maps product service codes that should be hidden when their feature flag is enabled
 * (the inverse of PRODUCT_CARD_FEATURE_FLAGS). Used for cards that are being replaced
 * by a newer variant gated behind the same flag, so the legacy card disappears at the
 * same moment the new one appears.
 *
 * @type {Record<string, string>}
 * @example
 * // 'domain_name_reg' is shown only while RELEASE_SQUARESPACE_DOMAINS_RESELLER is false;
 * // once the flag is enabled, 'domain_name_reg_sqsp' takes its place.
 */
export const PRODUCT_CARD_DISABLED_FEATURE_FLAGS = {
  [PRODUCT_SERVICE_NAME.DOMAIN_NAME_REG]: RELEASE_SQUARESPACE_DOMAINS_RESELLER,
  [PRODUCT_SERVICE_NAME.ONLINE_TOOLS_BUNDLE]: RELEASE_SQUARESPACE_WEBSITES_RESELLER,
  [PRODUCT_SERVICE_NAME.BASIC_EMAIL]: RELEASE_SQUARESPACE_WEBSITES_RESELLER,
  [PRODUCT_SERVICE_NAME.WEBSITE]: RELEASE_SQUARESPACE_WEBSITES_RESELLER,
};

/**
 * Hook to retrieve the feature flag values for the product cards
 * @returns {object} An object with the feature flag values for the product cards
 */
export function useProductCardFeatureFlags() {
  const { entitlements } = useBusinessEntity();
  const enableSCorpBundleV1 = useExperimentFlag(ENABLE_SCORP_BUNDLE_V1, false);
  const enableSalesTaxProduct = useExperimentFlag(ENABLE_SALES_TAX_PRODUCT, false);
  const rawReleaseSquarespaceDomainsReseller = useExperimentFlag(RELEASE_SQUARESPACE_DOMAINS_RESELLER, false);
  const { isSqspEligible, loading: websiteEligibilityLoading } = useWebsiteEligibility();

  // Require both the LD flag AND the sqsp entitlement — mirrors websites-ui DomainListPage logic.
  // Users with only domain_name_reg (OpenSRS/webtools bundle) must not see the Sqsp card.
  const releaseSquarespaceDomainsReseller =
    rawReleaseSquarespaceDomainsReseller && entitlements.has(PRODUCT_SERVICE_NAME.DOMAIN_NAME_REG_SQSP);
  const releaseSquarespaceWebsitesReseller = !websiteEligibilityLoading && isSqspEligible;

  return useMemo(
    () => ({
      [ENABLE_SCORP_BUNDLE_V1]: enableSCorpBundleV1,
      [ENABLE_SALES_TAX_PRODUCT]: enableSalesTaxProduct,
      [RELEASE_SQUARESPACE_DOMAINS_RESELLER]: releaseSquarespaceDomainsReseller,
      [RELEASE_SQUARESPACE_WEBSITES_RESELLER]: releaseSquarespaceWebsitesReseller,
    }),
    [enableSCorpBundleV1, enableSalesTaxProduct, releaseSquarespaceDomainsReseller, releaseSquarespaceWebsitesReseller],
  );
}

/**
 * Function to filter the product cards by the feature flag values
 * @param {object[]} cards - The product cards
 * @param {object} featureFlagValues - The feature flag values
 * @returns {object[]} The filtered product cards
 */
export function filterProductCardsByFeatureFlags(cards, featureFlagValues) {
  if (!cards || !Array.isArray(cards)) {
    return [];
  }

  return cards.filter((card) => {
    if (!card || !card.serviceCode) {
      return true;
    }

    const requiredFlag = PRODUCT_CARD_FEATURE_FLAGS[card.serviceCode];
    if (requiredFlag && featureFlagValues[requiredFlag] !== true) {
      return false;
    }

    const disabledFlag = PRODUCT_CARD_DISABLED_FEATURE_FLAGS[card.serviceCode];
    if (disabledFlag && featureFlagValues[disabledFlag] === true) {
      return false;
    }

    return true;
  });
}
